import cx from 'classnames'
import type React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled, { css } from 'styled-components'

import { isFieldHighlighted } from '@helpers'

import { InputLabel } from '../InputElements'
import { FormControl } from './FormControl'
import { FormHelperText } from './FormHelperText'
import type { DefaultSelectInputOption, SelectInputProps } from './SelectInputs'
import { SelectInput } from './SelectInputs'

const StyledFormControl = styled(FormControl)<{ $isAutoTag?: boolean }>`
  ${({ $isAutoTag }) =>
    $isAutoTag &&
    css`
      & .react-select__multi-value {
        background-color: ${({ theme }) => theme.colors.common.lightPurple};
      }
      & .react-select__multi-value__label,
      & .react-select__multi-value__remove {
        color: ${({ theme }) => theme.colors.common.darkPurple};
      }
    `}
`

export interface ReactHookFormSelectFieldProps<Option extends DefaultSelectInputOption>
  extends Omit<SelectInputProps<Option>, 'value' | 'onChange' | 'onBlur'> {
  disabled?: boolean
  highlighted?: boolean
  isLabelHighlighted?: boolean
  label?: React.ReactNode
  onChange?: (name: string, value: any) => void
  onBlur?: VoidFunction
  required?: boolean
  selectVariant?: 'blue' | 'purple'
  SelectComponent?: React.ComponentType<SelectInputProps<Option>>
}

export function ReactHookFormSelectField<Option extends DefaultSelectInputOption>({
  // recommendation, // TODO later need
  className,
  disabled = false,
  highlighted = false,
  isLabelHighlighted = false,
  label,
  name,
  onBlur: onBlurCallback,
  onChange: onChangeCallback,
  required,
  selectVariant = 'blue',
  SelectComponent = SelectInput,
  ...rest
}: ReactHookFormSelectFieldProps<Option>) {
  const {
    control,
    formState: { isSubmitting },
  } = useFormContext()
  const isFieldDisabled = disabled || isSubmitting

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
        const hasError = !!error
        return (
          <StyledFormControl className={className} error={hasError} $isAutoTag={selectVariant === 'purple'}>
            {label && (
              <InputLabel error={hasError} required={required} highlighted={isLabelHighlighted} htmlFor={name}>
                {label}
              </InputLabel>
            )}
            <SelectComponent
              className={cx({
                error: hasError,
                highlighted: isFieldHighlighted(highlighted, value),
              })}
              isDisabled={isFieldDisabled}
              name={name}
              onChangeCallback={onChangeCallback}
              // input controls
              onBlur={() => {
                onBlur()
                onBlurCallback?.()
              }}
              onChange={onChange}
              value={value}
              {...rest}
            />
            {error?.message && <FormHelperText>{error?.message}</FormHelperText>}
          </StyledFormControl>
        )
      }}
    />
  )
}
